import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { api, ApiError } from "./lib/api.js";
import { Login } from "./pages/Login.js";
import { DeckPage } from "./pages/Deck.js";
import { ShortlistPage } from "./pages/Shortlist.js";
import { StatsPage } from "./pages/Stats.js";
import { SettingsPage } from "./pages/Settings.js";
import { OrbitPage } from "./pages/Orbit.js";
import { FollowingPage } from "./pages/Following.js";
import { Spinner } from "./components/ui.js";

/**
 * Le rendu GL de la shortlist (§D42, §D43), en chargement DIFFÉRÉ.
 *
 * Il embarque three, une bonne centaine de kilo-octets compressés. Le deck
 * reste la page d'atterrissage : `lazy` garde three dans son propre morceau,
 * payé à la première visite de la shortlist seulement.
 */
const ShortlistGLPage = lazy(() =>
  import("./pages/ShortlistGL.js").then((m) => ({ default: m.ShortlistGLPage })),
);

/**
 * La shortlist, GL PAR DÉFAUT (décision de Vincent, 14/09/2026) — le signet
 * d'écran d'accueil ne peut pas porter de drapeau, le défaut doit être le bon
 * moteur. `?gl=0` rend la référence DOM, toujours dans le repo (§D42) ;
 * `?vec=1` continue de choisir le contenu vectoriel côté GL (§D44).
 */
function ShortlistRoute() {
  const dom = new URLSearchParams(useLocation().search).get("gl") === "0";
  if (dom) return <ShortlistPage />;
  return (
    <Suspense fallback={<Spinner />}>
      <ShortlistGLPage />
    </Suspense>
  );
}

/**
 * L'onglet Orbit devient « Suivis » (décision de Vincent, 14/09/2026, §D49) :
 * optimiser qui je suis rapporte plus, au quotidien, que le kanban de
 * réciprocité. Celui-ci reste entier derrière `?pairs=1` — il n'est pas
 * abandonné, il n'est simplement plus la porte d'entrée. Même ligne de garde
 * que `ShortlistRoute` (§D48), et pour la même raison : une URL, deux écrans,
 * zéro duplication de route.
 */
function FollowingRoute() {
  const pairs = new URLSearchParams(useLocation().search).get("pairs") === "1";
  return pairs ? <OrbitPage /> : <FollowingPage />;
}

type AuthState = "checking" | "in" | "out";

export function App() {
  const [auth, setAuth] = useState<AuthState>("checking");

  const check = useCallback(async () => {
    try {
      const { authenticated } = await api.get<{ authenticated: boolean }>("/auth/session");
      setAuth(authenticated ? "in" : "out");
    } catch {
      setAuth("out");
    }
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  /**
   * Une session qui expire pendant l'usage doit ramener au login sans laisser
   * l'app dans un état à moitié cassé. On écoute donc les 401 globalement.
   */
  useEffect(() => {
    const onRejection = (event: PromiseRejectionEvent) => {
      if (event.reason instanceof ApiError && event.reason.status === 401) {
        setAuth("out");
      }
    };
    window.addEventListener("unhandledrejection", onRejection);
    return () => window.removeEventListener("unhandledrejection", onRejection);
  }, []);

  if (auth === "checking") return <Spinner />;
  if (auth === "out") return <Login onSuccess={() => setAuth("in")} />;

  /**
   * Coquille à hauteur connue.
   *
   * `100dvh` et non `100vh` : sur mobile, la barre d'URL qui se rétracte fait
   * varier la hauteur réelle, et `vh` fige la valeur haute — le bas de l'app
   * passait sous la barre système. `dvh` suit la zone réellement visible.
   *
   * MOINS l'encoche : le `body` porte `padding-top: env(safe-area-inset-top)`
   * (le contenu ne doit pas passer sous la barre système). En Safari nu cet
   * inset vaut 0 et tout semblait juste ; en PWA plein écran (signet d'écran
   * d'accueil, `viewport-fit=cover`) il vaut la hauteur de l'encoche, et
   * padding + 100dvh faisait déborder le document d'autant — la barre
   * d'onglets passait sous le pli, il fallait défiler pour la voir.
   *
   * La barre d'onglets est dans le flux, plus en `fixed` : c'est ce qui permet
   * à `main` d'avoir une hauteur bornée, donc aux pages de répartir leur espace
   * vertical au lieu de déborder.
   */
  return (
    <div className="mx-auto flex h-[calc(100dvh-env(safe-area-inset-top,0px))] max-w-lg flex-col overflow-hidden">
      {/* overflow-x-hidden : le premier plan de la shortlist a le droit de
          s'évaser au-delà de la vitre (§D35), pas de créer un défilement
          horizontal. */}
      <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
        <Routes>
          <Route path="/" element={<DeckPage />} />
          <Route path="/shortlist" element={<ShortlistRoute />} />
          <Route path="/stats" element={<StatsPage />} />
          <Route path="/orbit" element={<FollowingRoute />} />
          <Route path="/settings" element={<SettingsPage onLogout={() => setAuth("out")} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Nav />
    </div>
  );
}

const TABS = [
  { to: "/", label: "Deck" },
  { to: "/shortlist", label: "Shortlist" },
  { to: "/orbit", label: "Suivis" },
  { to: "/stats", label: "Stats" },
  { to: "/settings", label: "Réglages" },
];

function Nav() {
  return (
    <nav
      className="shrink-0 border-t border-[var(--color-line)] bg-[var(--color-ink)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="flex">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.to === "/"}
            className={({ isActive }) =>
              `flex-1 py-3 text-center text-xs font-medium transition ${
                isActive ? "text-[var(--color-accent)]" : "text-[var(--color-muted)]"
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
